import { ListOnScrollProps, VariableSizeList } from 'react-window';
import { RefObject, useCallback, useLayoutEffect, useState } from 'react';

import { useLogViewerContext } from '../context';

export function useKeepAtBottom(
  outerRef: RefObject<HTMLDivElement>,
  listRef: RefObject<VariableSizeList>
) {
  const [isScrollAtBottom, setIsScrollAtBottom] = useState(false);
  const [keepAtBottom, setKeepAtBottom] = useState(false);

  const { logs, setVisibleStartIndex } = useLogViewerContext();

  const checkIsScrollAtBottom = useCallback(() => {
    const ref = outerRef.current;
    if (ref) {
      const isBottom = ref.scrollHeight - ref.scrollTop - ref.clientHeight < 10;
      setIsScrollAtBottom(isBottom);
      return isBottom;
    }
    return false;
  }, [outerRef]);

  const onItemsRendered = useCallback(
    ({ visibleStartIndex }: { visibleStartIndex: number }) => {
      setVisibleStartIndex(visibleStartIndex);
      checkIsScrollAtBottom();
    },
    [checkIsScrollAtBottom, setVisibleStartIndex]
  );

  const onScroll = useCallback(
    ({ scrollDirection, scrollUpdateWasRequested }: ListOnScrollProps) => {
      const isAtBottom = checkIsScrollAtBottom();

      // our own scrollToBottom - it must neither arm nor disarm stickiness
      if (scrollUpdateWasRequested) {
        return;
      }

      // Scrolling up means the user wants to read something. Deciding from the bottom
      // threshold alone re-sticks the view during the first pixels of a trackpad
      // gesture, before momentum has carried it clear of the threshold.
      if (scrollDirection === 'backward') {
        setKeepAtBottom(false);
        return;
      }

      setKeepAtBottom(isAtBottom);
    },
    [checkIsScrollAtBottom]
  );

  const logLength = logs.logs.length;

  const scrollToBottom = useCallback(() => {
    setKeepAtBottom(true);
    if (logLength > 0) {
      const lastIndex = logLength - 1;
      // to handle large logs, scroll to the end of the last log, not the start of the last log
      listRef.current?.scrollToItem(lastIndex, 'end');
    }
  }, [logLength, listRef]);

  useLayoutEffect(() => {
    if (keepAtBottom) {
      scrollToBottom();
    }
  }, [keepAtBottom, logLength, scrollToBottom]);

  return { isScrollAtBottom, onItemsRendered, onScroll, scrollToBottom };
}
